/**
 * Guide texts that belong to the role-based structure: the head of each tab and the topics added with the start
 * page, quick booking, people, customers, announcements and the background jobs. The older topics are in
 * help-content.ts (employees) and help-lead.ts (leads, office, administration). Which topic is shown in which tab
 * is decided in one place, GUIDE_TOPICS in @pz/core. Keep the three languages in the same structure;
 * `**text**` is printed bold.
 */
import type { GuideAudience } from '@pz/core';
import type { HelpStep } from './help-content';

type Item = { title: string; text: string };

export interface TabText {
  /** label of the tab */
  label: string;
  kicker: string;
  title: string;
  subtitle: string;
  /** who the tab is for and how it relates to the employee tab */
  hint: string;
}

export interface TopicsText {
  tabs: Record<GuideAudience, TabText>;
  /** mark on the tab that matches the role of the person */
  ownRole: string;
  /** label of the jump list in long tabs */
  contents: string;
  home: { title: string; lead: string; items: [HelpStep, HelpStep, HelpStep, HelpStep]; note: string };
  external: { title: string; lead: string; items: [HelpStep, HelpStep, HelpStep, HelpStep]; note: string };
  quick: { title: string; lead: string; items: [HelpStep, HelpStep, HelpStep, HelpStep]; note: string };
  people: { title: string; lead: string; items: [HelpStep, HelpStep, HelpStep, HelpStep]; note: string };
  customers: { title: string; lead: string; items: [Item, Item, Item]; note: string };
  publish: { title: string; lead: string; steps: [HelpStep, HelpStep, HelpStep, HelpStep]; points: [string, string] };
  automation: { title: string; lead: string; items: [Item, Item, Item, Item, Item, Item]; note: string };
  /** request 37 (round 5): IT requests through Teams for now; remove with the topic when a module for them exists */
  itNote: { title: string; text: string };
}

export const TOPICS: Record<'de' | 'en' | 'tr', TopicsText> = {
  de: {
    ownRole: 'Ihre Rolle',
    contents: 'In diesem Reiter',
    tabs: {
      external: {
        label: 'Externe',
        kicker: 'Kurzanleitung für externe Partner',
        title: 'Projektzeit für Externe',
        subtitle: 'Buchen auf den Projekten und Aufgaben, die Ihnen zugewiesen sind.',
        hint: 'Für externe Partner und freie Mitarbeitende. Sie sehen nur, was Ihnen zugewiesen ist; alles andere bleibt verborgen.',
      },
      employee: {
        label: 'Mitarbeitende',
        kicker: 'Kurzanleitung für Mitarbeitende',
        title: 'Projektzeit im Arbeitsalltag',
        subtitle: 'Zeiterfassung auf Forma-Aufgaben, Freigabe, Planung und Auswertung.',
        hint: 'Das Wichtigste für jeden Tag. Es gilt für alle, auch für Projektleitung, Büro und Geschäftsführung, wenn sie selbst Zeit buchen.',
      },
      lead: {
        label: 'Projektleitung',
        kicker: 'Kurzanleitung für die Projektleitung',
        title: 'Projektzeit für die Projektleitung',
        subtitle: 'Zeiten Ihrer Projekte freigeben, Plan-Stunden pflegen, Projekte auswerten und Forma-Aufgaben steuern.',
        hint: 'Zusätzlich zu den Grundlagen im Reiter „Mitarbeitende“. Projektleitung gilt je Projekt (Projekt > Team); Sie sehen die Daten Ihrer Projekte.',
      },
      office: {
        label: 'Büro und Geschäftsführung',
        kicker: 'Leitfaden für Büro, Personal und Geschäftsführung',
        title: 'Projektzeit für Büro, Personal und Geschäftsführung',
        subtitle: 'Freigaben, Personal, Planung, Auswertung, Abrechnung, Kunden, Monatsabschluss und Mitteilungen.',
        hint: 'Zusätzlich zu den Grundlagen im Reiter „Mitarbeitende“. Welche Seiten Sie sehen, bestimmen Ihre Rechte; hier steht, was die Rolle tut.',
      },
      admin: {
        label: 'Technischer Admin',
        kicker: 'Leitfaden für die technische Administration',
        title: 'Projektzeit einrichten und betreiben',
        subtitle: 'Zugänge, Einstellungen, Forma-Abgleich, Sicherung und Hintergrundaufgaben.',
        hint: 'Die technische Administration richtet das System ein und betreibt es. Stunden anderer, Sätze, Personaldaten und Kennzahlen sieht sie nur mit dem jeweiligen Recht.',
      },
    },
    home: {
      title: 'Startseite, Mitteilungen und Benachrichtigungen',
      lead: 'Was heute zu tun ist, was ansteht und was es Neues gibt, nach Ihren Rechten.',
      items: [
        {
          title: 'Übersicht',
          text: 'Ihre Startseite. Oben Kacheln für das, was Sie oft tun (Zeit erfassen, Meine Woche, je nach Recht Freigaben, Planung, Abrechnung). **Meine Aufgaben** zeigt nur, was auf Sie wartet, z. B. Woche einreichen, zurückgewiesene Einträge, Anträge zur Entscheidung.',
        },
        {
          title: 'Demnächst',
          text: 'Eigene Abwesenheiten, Feiertage Ihres Standorts, Firmentermine und Fristen, Frist zum Einreichen der Woche und das Ende Ihrer Projekte. Geburtstage nur von Personen, die sie selbst freigegeben haben.',
        },
        {
          title: 'Mitteilungen',
          text: 'Neuigkeiten der Firma. „Neu“ markiert Ungelesenes; bei Unterweisungen bestätigen Sie mit **Zur Kenntnis genommen**. Dringendes und Angeheftetes steht oben. „Firmenstandards und Vorlagen“ führt zur Seite der Firma, dort liegen die Dokumente.',
        },
        {
          title: 'Glocke',
          text: 'Rechts oben: Hinweise, nach Art farbig (Zeiten, Abwesenheiten, Freigaben, System …) und nach Ihren Rechten. Ihr Filter wird im Konto gespeichert und gilt auf jedem Gerät. **Mein Verlauf** zeigt Ihre eigenen letzten Aktionen.',
        },
      ],
      note: 'Hinweise entstehen beim Öffnen der Seite; für sie werden keine E-Mails verschickt. Eine Mitteilung kommt zusätzlich per E-Mail, wenn die Firma das eingeschaltet hat und die veröffentlichende Person es wählt. Im Verlauf steht zu Daten anderer Personen nur die Art der Aktion, nie der Inhalt.',
    },
    quick: {
      title: 'Schnell buchen',
      lead: 'Wer jede Woche ähnliche Aufgaben bucht, braucht keine Suche mehr.',
      items: [
        { title: 'Zuletzt genutzt', text: 'Im Dialog „Zeit erfassen“ stehen Ihre zuletzt gebuchten Aufgaben oben: ein Klick wählt die Aufgabe, dann nur noch Stunden eintragen.' },
        { title: 'Favoriten', text: 'Mit dem **Stern** merken Sie Aufgaben, die Sie oft buchen (bis 30). Sie stehen im Dialog unter „Favoriten“ und sind nur für Sie sichtbar.' },
        {
          title: 'Mehrere Tage',
          text: '**Mehrere Tage buchen**: dieselbe Aufgabe mit gleichen Stunden und Tätigkeit an mehreren Tagen, z. B. eine Woche Baustelle. Vor dem Buchen sehen Sie jeden Tag; Feiertage, Abwesenheiten und schon gebuchte Tage sind nicht angehakt.',
        },
        {
          title: 'Woche übernehmen',
          text: 'In Meine Woche übernehmen Sie die letzte (oder eine andere) Woche oder eine **Vorlage** (bis 20, nur für Sie) als Entwürfe. Sie prüfen jede Zeile und reichen wie gewohnt ein.',
        },
      ],
      note: 'Es gelten dieselben Regeln wie bei jeder Buchung: Stundenraster, geschlossene Monate und die Pflicht zur Tätigkeit bei abrechenbaren Stunden. Ohne Ihre Vorschau wird nichts gebucht.',
    },
    external: {
      title: 'Als externer Partner arbeiten',
      lead: 'Ihr Konto ist auf die Projekte und Aufgaben beschränkt, die Ihnen die Projektleitung oder das Büro zuweist.',
      items: [
        { title: 'Was Sie sehen', text: 'Ihre zugewiesenen Projekte und Aufgaben, Ihre eigenen Zeiten und Ihre Woche. Stunden anderer, Sätze, Kennzahlen und Kundendaten sehen Sie nicht.', who: 'Extern' },
        { title: 'Zeiten buchen', text: 'Aufgabe wählen, Stunden und Tätigkeit eintragen, die Woche einreichen. Entwürfe sieht nur Ihr Konto, bis Sie sie einreichen.', who: 'Extern' },
        { title: 'Freigabe', text: 'Die Projektleitung prüft Ihre Zeiten. Bei einer Zurückweisung steht die Begründung am Eintrag: korrigieren und neu einreichen.', who: 'Projektleitung' },
        { title: 'Aufgabe fehlt?', text: 'Aufgaben weist die Projektleitung oder das Büro zu. Fehlt eine, wenden Sie sich an die Projektleitung des Projekts.', who: 'Projektleitung, Büro' },
      ],
      note: 'Es gelten dieselben Regeln wie für alle: Stundenraster, geschlossene Monate und die Tätigkeit bei abrechenbaren Stunden.',
    },
    people: {
      title: 'Personen, Kürzel und Zugang',
      lead: 'Verwaltung > Personen: ein Bereich mit zwei Reitern, „Personen“ (Stammdaten, Rolle, Zugang) und „Abwesenheiten“ (Ansprüche, Salden, Kalender).',
      items: [
        {
          title: 'Stammdaten',
          text: 'Name, Fachbereich, Standort mit Feiertagskalender, Eintritt und Austritt, Arbeitszeit ab einem Datum und die **genehmigende Person** für Abwesenheiten. Die Seite sehen nur Personal und Benutzerverwaltung.',
          who: 'Personal',
        },
        {
          title: 'Rolle und Zugang',
          text: 'Rolle wählen: Sie setzt Standardrechte, einzelne lassen sich entfernen oder hinzufügen; die Berufsbezeichnung ist davon unabhängig. Rollen und Rechte ändern nur Administration und Geschäftsführung. Passwörter vergibt niemand: „Zugang zurücksetzen“ erzeugt einen Einrichtungslink (7 Tage, einmal verwendbar). Konten mit geschützten Rechten ändert nur, wer diese Rechte selbst hat.',
          who: 'Personal, Admin',
        },
        {
          title: 'Kürzel',
          text: 'Jede Person hat ein festes, im ganzen Unternehmen **eindeutiges Kürzel** aus 2 bis 6 Buchstaben oder Ziffern: die Initialen (z. B. HA); ist es vergeben, kommt ein Konsonant dazu (HAL). Es bleibt gleich, auch wenn Personen dazukommen oder gehen. „Fehlende Kürzel vergeben“ füllt Lücken.',
          who: 'Personal',
        },
        {
          title: 'Geburtstage',
          text: 'Freiwillig: Jede Person trägt Tag und Monat selbst im **Profil** ein und entscheidet, ob andere ihn sehen (Teamkalender, Demnächst). Es gibt bewusst keine Gesamtliste und keinen Export.',
        },
      ],
      note: 'Abwesenheiten, Urlaubskonten und Feiertage sind der zweite Reiter derselben Rubrik, weil dieselben Personen dahinterstehen. Die Rechte bleiben getrennt: „Personen“ öffnet mit Personal oder Benutzer und Rollen, „Abwesenheiten“ mit Personal oder Einstellungen.',
    },
    customers: {
      title: 'Kunden und Zahlungsbedingungen',
      lead: 'Leitung > Kunden, mit dem Recht „Kundenstamm und Konditionen“. Eine schlanke Kontaktliste; keine Buchhaltung und keine Rechnungsstellung.',
      items: [
        {
          title: 'Kontakte und Firmen',
          text: 'Firma, Anschrift (Ort, Land), Ansprechpartner mit Funktion, E-Mail, Telefon, **Status** (Interessent, Angebot abgegeben, Aktiver Kunde, Ehemaliger Kunde, Geschäftspartner, Nicht kontaktieren) und Notiz. Suchen, nach Status filtern, CSV importieren und exportieren.',
        },
        {
          title: 'Projekte der Firma',
          text: 'Je Firma die zugeordneten Projekte mit Rolle (Auftraggeber, Rechnungsempfänger …), Stunden der letzten 12 Monate, nicht abgerechneter Leistung und Belegen (netto). „Nicht kontaktieren“ heißt: keine E-Mails, keine Anrufe.',
        },
        {
          title: 'Zahlungsbedingungen',
          text: 'Je Projekt (Reiter „Konditionen“): **Zahlungsziel** (z. B. 14 Tage), **Skonto** (z. B. 1,5 % bei Zahlung in 7 Tagen) und **Abzüge nach der Umsatzsteuer** (Nachlass, Sicherheitseinbehalt), jeweils auf den Gesamtbetrag inklusive Umsatzsteuer. Ein neuer Abschlag übernimmt sie; das Büro kann sie im Dokument ändern. Der Stundennachweis ist ein Leistungsnachweis und enthält keine Zahlungsbedingungen.',
        },
      ],
      note: 'Ob und wie die Umsatzsteuer bei Skonto korrigiert wird, entscheidet der Steuerberater (§ 17 UStG). Die Beispielrechnung zeigt nur, was der Kunde zahlt; die Rechnung selbst entsteht in der Buchhaltung.',
    },
    publish: {
      title: 'Mitteilungen veröffentlichen',
      lead: 'Verwaltung > Mitteilungen, nur mit dem Recht „Mitteilungen veröffentlichen“ (Geschäftsführung, auf Wunsch weitere Personen). Alle anderen lesen sie auf der Übersicht.',
      steps: [
        {
          title: 'Art und Text',
          text: 'Information, Termin, Betriebsschließung, Frist, Unterweisung, IT und System oder Richtlinie geändert. Termine, Schließungen und Fristen erscheinen auch unter „Demnächst“.',
          who: 'Art',
        },
        {
          title: 'Zielgruppe',
          text: 'Alle Mitarbeitenden, Standorte, Rollen (auch „Projektleitung“), Projektteams oder **einzelne Personen** (z. B. eine Mitteilung nur für eine Person). Wer zu einer Gruppe gehört, sieht die Mitteilung; Externe nur, was ihre Rolle, ihr Projekt oder sie selbst betrifft.',
          who: 'Wer',
        },
        {
          title: 'Zeitraum und Rang',
          text: 'Sichtbar ab und bis; Wichtigkeit normal, wichtig oder dringend; **Anheften** hält sie oben. **Wiederholen** für Wiederkehrendes (wöchentlich, monatlich, jährlich), mit „Vorher anzeigen“.',
          who: 'Wann',
        },
        {
          title: 'E-Mail und Kenntnisnahme',
          text: '**Auch per E-Mail senden** (wenn in den Einstellungen eingeschaltet; bei jeder neuen Mitteilung zunächst nicht gesetzt): an die Firmenadressen der erreichten Personen, auch externer, sobald die Mitteilung sichtbar wird; Stand und Vorschau unter „E-Mail“. Für Unterweisungen „Kenntnisnahme bestätigen lassen“: Sie sehen, wer noch nicht bestätigt hat. Bei allen anderen Mitteilungen sehen Sie nur Zahlen (gelesen von …), keine Namen.',
          who: 'Nachweis',
        },
      ],
      points: [
        '**Firmenstandards bleiben außerhalb**: Richtlinien, Vorlagen und Layouts liegen dort, wo die Firma sie ablegt (z. B. SharePoint). Eine Mitteilung verweist mit einem **Link** darauf; die Übersicht zeigt zusätzlich „Firmenstandards und Vorlagen“ (Adresse unter Verwaltung > Einstellungen).',
        '**Kurz halten**: eine Aussage je Mitteilung, bei „Richtlinie geändert“ sagen, was sich ändert, und auf das Dokument verlinken. Unterweisungen mit Bestätigung und Wiederholung eignen sich für Arbeitsschutz und Datenschutz.',
      ],
    },
    automation: {
      title: 'Hintergrundaufgaben und Automatisierung',
      lead: 'Die Plattform erledigt Routinearbeit selbst, ohne externen Dienst und ohne Zusatzlizenz.',
      items: [
        { title: 'Forma-Abgleich', text: 'Im eingestellten Rhythmus und Zeitfenster (Standard: alle 15 Minuten, täglich 6 bis 24 Uhr). Dauer und Anfragen je Tag: Verwaltung > Forma.' },
        { title: 'Outlook', text: 'Alle 5 Minuten, wenn die Anbindung eingeschaltet ist: genehmigte Abwesenheiten in die persönlichen Kalender und den Teamkalender.' },
        {
          title: 'Nachtlauf 2:30 Uhr',
          text: 'Vollständiger Forma-Abgleich, neue Projekte übernehmen (wenn eingeschaltet), Momentaufnahme der Datenbank (35 Nächte), Kapazitätswerte, Power-BI-Export (wenn eingerichtet), Aufbewahrungsfristen für Nachweise, abgelaufene Sitzungen.',
        },
        {
          title: 'Erinnerungen',
          text: 'Vier Regeln (Verwaltung > Einstellungen > Automatisierungen), einzeln ein- und ausschaltbar: Frist zum Einreichen der Woche, Monatsabschluss, fehlende Buchungen, wartende Freigaben. Sie erscheinen als Hinweis bei der betroffenen Person.',
        },
        { title: 'Wiederkehrende Mitteilungen', text: 'Unterweisungen, Fristen oder Hinweise wiederholen sich wöchentlich, monatlich oder jährlich (Mitteilungen). Jede Wiederholung zählt für die Bestätigung neu.' },
        {
          title: 'Grenzen',
          text: 'Jede Regel zeigt einer Person nur, was sie ohnehin sehen darf; keine Automatisierung hat mehr Rechte als die Rolle, die sie auslöst, und personenbezogene Daten gehen nie an Unberechtigte.',
        },
      ],
      note: 'Was nicht automatisch geschieht: periodische Berichte per E-Mail und Abläufe mit fremden Systemen (z. B. Power Automate). Das ist Absicht: kein Versand personenbezogener Daten ohne geklärten Empfängerkreis und keine Zusatzlizenz. Excel-Berichte holen Berechtigte selbst ab. Einzige E-Mail: Mitteilungen, wenn „Mitteilungen per E-Mail“ eingeschaltet ist (alle 5 Minuten die fälligen, Recht „Application Mail.Send“ nur für die Absender-Postfächer).',
    },
    itNote: {
      title: 'Hinweis: IT-Anfragen',
      text: 'Anfragen an die IT (Zugang, Gerät, Software, Störung) richten Sie bitte vorerst über **Teams** an die IT. Projektzeit nimmt sie noch nicht entgegen.',
    },
  },

  en: {
    ownRole: 'Your role',
    contents: 'In this tab',
    tabs: {
      external: {
        label: 'External',
        kicker: 'Quick guide for external partners',
        title: 'Projektzeit for external partners',
        subtitle: 'Booking on the projects and tasks assigned to you.',
        hint: 'For external partners and freelancers. You see only what is assigned to you; everything else stays hidden.',
      },
      employee: {
        label: 'Employees',
        kicker: 'Quick guide for employees',
        title: 'Projektzeit in everyday work',
        subtitle: 'Time tracking on Forma tasks, approval, planning and analysis.',
        hint: 'What matters every day. It applies to everyone, including project leads, office and management when they book their own time.',
      },
      lead: {
        label: 'Project lead',
        kicker: 'Quick guide for project leads',
        title: 'Projektzeit for project leads',
        subtitle: 'Approve the hours of your projects, maintain planned hours, analyse projects and steer Forma tasks.',
        hint: 'In addition to the basics in the "Employees" tab. Project lead applies per project (project > Team); you see the data of your projects.',
      },
      office: {
        label: 'Office & management',
        kicker: 'Guide for office, HR and management',
        title: 'Projektzeit for office, HR and management',
        subtitle: 'Approvals, HR, planning, analysis, billing, customers, month closing and announcements.',
        hint: 'In addition to the basics in the "Employees" tab. Your rights decide which pages you see; this tab describes what the role does.',
      },
      admin: {
        label: 'Technical admin',
        kicker: 'Guide for technical administration',
        title: 'Setting up and running Projektzeit',
        subtitle: 'Access, settings, Forma synchronisation, backup and background jobs.',
        hint: 'Technical administration sets up and runs the system. It sees the hours of others, rates, HR data and key figures only with the respective right.',
      },
    },
    home: {
      title: 'Start page, announcements and notifications',
      lead: 'What to do today, what is coming up and what is new, according to your rights.',
      items: [
        {
          title: 'Overview',
          text: 'Your start page. Tiles at the top for what you do often (record time, My week, depending on rights approvals, planning, billing). **My tasks** shows only what waits for you, e.g. submit the week, rejected entries, requests to decide.',
        },
        {
          title: 'Coming up',
          text: 'Your own absences, public holidays of your location, company events and deadlines, the deadline to submit the week and the end of your projects. Birthdays only of people who have released them themselves.',
        },
        {
          title: 'Announcements',
          text: 'News from the company. "New" marks what is unread; for instructions you confirm with **Noted**. Urgent and pinned ones come first. "Company standards and templates" leads to the company\'s own page, where the documents are kept.',
        },
        {
          title: 'Bell',
          text: 'Top right: notes, coloured by kind (hours, absences, approvals, system …) and according to your rights. Your filter is saved in your account and applies on every device. **My history** shows your own latest actions.',
        },
      ],
      note: 'Notes are created when the page opens; no e-mails are sent for them. An announcement also comes by e-mail when the company has switched this on and the publisher chooses it. The history shows only the kind of action for other people\'s data, never the content.',
    },
    quick: {
      title: 'Book quickly',
      lead: 'Whoever books similar tasks every week no longer needs to search.',
      items: [
        { title: 'Recently used', text: 'In the "Record time" dialog your latest tasks are on top: one click selects the task, then only enter the hours.' },
        { title: 'Favorites', text: 'Use the **star** to remember tasks you book often (up to 30). They appear in the dialog under "Favorites" and are visible only to you.' },
        {
          title: 'Several days',
          text: '**Book several days**: the same task with the same hours and description on several days, e.g. a week on site. Before booking you see every day; holidays, absences and days already booked are not ticked.',
        },
        {
          title: 'Copy a week',
          text: 'In My week you copy the last (or another) week, or a **template** (up to 20, only for you), as drafts. You check every row and submit as usual.',
        },
      ],
      note: 'The same rules apply as for any booking: hour grid, closed months and the duty to describe billable hours. Nothing is booked without your preview.',
    },
    external: {
      title: 'Working as an external partner',
      lead: 'Your account is limited to the projects and tasks the project lead or the office assigns to you.',
      items: [
        { title: 'What you see', text: 'Your assigned projects and tasks, your own time and your week. Hours of others, rates, key figures and customer data are not visible to you.', who: 'External' },
        { title: 'Booking time', text: 'Choose the task, enter hours and activity, submit the week. Only your account sees drafts until you submit them.', who: 'External' },
        { title: 'Approval', text: 'The project lead checks your time. When an entry is rejected, the reason is shown on it: correct it and submit again.', who: 'Project lead' },
        { title: 'Task missing?', text: 'Tasks are assigned by the project lead or the office. If one is missing, contact the project lead of the project.', who: 'Project lead, office' },
      ],
      note: 'The same rules apply as for everyone: hour grid, closed months and the activity for billable hours.',
    },
    people: {
      title: 'People, initials and access',
      lead: 'Administration > People: one area with two tabs, "People" (master data, role, access) and "Absences" (entitlements, balances, calendar).',
      items: [
        {
          title: 'Master data',
          text: 'Name, discipline, location with holiday calendar, entry and exit, working time from a date and the **approver** for absences. Only HR and user administration see the page.',
          who: 'HR',
        },
        {
          title: 'Role and access',
          text: 'Choose the role: it sets default rights; single ones can be removed or added, and the job title does not depend on them. Only administration and management change roles and rights. Nobody hands out passwords: "Reset access" creates a setup link (7 days, single use). Accounts with protected rights are changed only by someone who holds those rights.',
          who: 'HR, admin',
        },
        {
          title: 'Initials',
          text: 'Everybody has a fixed **initials code**, unique in the whole company, of 2 to 6 letters or digits: the initials (e.g. HA); if taken, a consonant is added (HAL). It stays the same when people join or leave. "Assign missing codes" fills gaps.',
          who: 'HR',
        },
        {
          title: 'Birthdays',
          text: 'Voluntary: everybody enters day and month in the **profile** and decides whether others see it (team calendar, Coming up). There is deliberately no complete list and no export.',
        },
      ],
      note: 'Absences, leave accounts and holidays are the second tab of the same section because the same people are behind them. The rights stay separate: "People" opens with HR or users and roles, "Absences" with HR or settings.',
    },
    customers: {
      title: 'Customers and payment terms',
      lead: 'Management > Customers, with the right "Customer master and terms". A lean contact list; no bookkeeping and no invoicing.',
      items: [
        {
          title: 'Contacts and companies',
          text: 'Company, address (city, country), contact person with position, e-mail, phone, **status** (Prospect, Offer submitted, Active customer, Former customer, Business partner, Do not contact) and note. Search, filter by status, import and export CSV.',
        },
        {
          title: 'Projects of the company',
          text: 'Per company the assigned projects with role (client, invoice recipient …), hours of the last 12 months, unbilled work and documents (net). "Do not contact" means: no e-mails, no calls.',
        },
        {
          title: 'Payment terms',
          text: 'Per project (tab "Terms"): **payment term** (e.g. 14 days), **early-payment discount** (e.g. 1.5 % if paid within 7 days) and **deductions after VAT** (allowance, retention), each on the total including VAT. A new progress invoice (Abschlag) takes them over; the office can change them in the document. The timesheet (Stundennachweis) is proof of work and carries no payment terms.',
        },
      ],
      note: 'Whether and how VAT is corrected for a discount is decided by the tax advisor (§ 17 UStG). The example calculation only shows what the customer pays; the invoice itself is created in bookkeeping.',
    },
    publish: {
      title: 'Publish announcements',
      lead: 'Administration > Announcements, only with the right "Publish announcements" (management, other people on request). Everybody else reads them on the overview.',
      steps: [
        {
          title: 'Kind and text',
          text: 'Information, event, office closure, deadline, instruction, IT and system, or policy changed. Events, closures and deadlines also appear under "Coming up".',
          who: 'Kind',
        },
        {
          title: 'Audience',
          text: 'All employees, locations, roles (also "Project lead"), project teams or **individual people** (e.g. an announcement for one person only). Whoever belongs to a group sees the announcement; externals only what concerns their role, their project or themselves.',
          who: 'Who',
        },
        {
          title: 'Period and rank',
          text: 'Visible from and until; importance normal, important or urgent; **Pin** keeps it on top. **Repeat** for recurring things (weekly, monthly, yearly), with "Show in advance".',
          who: 'When',
        },
        {
          title: 'E-mail and acknowledgement',
          text: '**Also send by e-mail** (when switched on in the settings; unticked for every new announcement): to the company addresses of the people reached, external people included, as soon as the announcement becomes visible; state and preview under "E-mail". For instructions use "Ask for confirmation": you see who has not confirmed yet. For all other announcements you see only numbers (read by …), no names.',
          who: 'Proof',
        },
      ],
      points: [
        '**Company standards stay outside**: policies, templates and layouts live where the company keeps them (e.g. SharePoint). An announcement points to them with a **link**; the overview also shows "Company standards and templates" (address under Administration > Settings).',
        '**Keep it short**: one message per announcement; for "Policy changed" say what changes and link the document. Instructions with confirmation and repetition suit safety and data protection.',
      ],
    },
    automation: {
      title: 'Background jobs and automation',
      lead: 'The platform does routine work itself, without an external service and without an extra licence.',
      items: [
        { title: 'Forma synchronisation', text: 'At the set rhythm and time window (default: every 15 minutes, daily 6 am to midnight). Duration and requests per day: Administration > Forma.' },
        { title: 'Outlook', text: 'Every 5 minutes when the connection is switched on: approved absences into the personal calendars and the team calendar.' },
        {
          title: 'Nightly run 2:30 am',
          text: 'Complete Forma synchronisation, import new projects (when switched on), database snapshot (35 nights), capacity values, Power BI export (when set up), retention periods for certificates, expired sessions.',
        },
        {
          title: 'Reminders',
          text: 'Four rules (Administration > Settings > Automations), each switched on or off: deadline to submit the week, month closing, missing bookings, waiting approvals. They appear as a note for the person concerned.',
        },
        { title: 'Recurring announcements', text: 'Instructions, deadlines or notes repeat weekly, monthly or yearly (Announcements). Every repetition counts anew for the confirmation.' },
        {
          title: 'Limits',
          text: 'Every rule shows a person only what they may see anyway; no automation has more rights than the role that triggers it, and personal data never goes to unauthorised people.',
        },
      ],
      note: 'What does not happen automatically: periodic reports by e-mail and flows with other systems (e.g. Power Automate). This is on purpose: no sending of personal data without a settled group of recipients, and no extra licence. Authorised people fetch Excel reports themselves. The only e-mail: announcements, when "Announcements by e-mail" is switched on (the due ones every 5 minutes, right "Application Mail.Send" for the sender mailboxes only).',
    },
    itNote: {
      title: 'Note: IT requests',
      text: 'For now, please send requests to IT (access, device, software, fault) via **Teams**. Projektzeit does not take them yet.',
    },
  },

  tr: {
    ownRole: 'Rolünüz',
    contents: 'Bu sekmede',
    tabs: {
      external: {
        label: 'Harici',
        kicker: 'Harici çalışanlar için kısa kılavuz',
        title: 'Harici çalışanlar için Projektzeit',
        subtitle: 'Size atanan proje ve görevlere zaman kaydı.',
        hint: 'Harici iş ortakları ve serbest çalışanlar için. Yalnızca size atananları görürsünüz; diğer her şey gizli kalır.',
      },
      employee: {
        label: 'Çalışanlar',
        kicker: 'Çalışanlar için kısa kılavuz',
        title: 'Günlük işlerde Projektzeit',
        subtitle: 'Forma görevlerine zaman kaydı, onay, planlama ve değerlendirme.',
        hint: 'Her gün işinize yarayan temel bilgiler. Kendi saatini giren herkes için geçerlidir; proje lideri, ofis ve yönetim dahil.',
      },
      lead: {
        label: 'Proje lideri',
        kicker: 'Proje liderleri için kısa kılavuz',
        title: 'Proje liderleri için Projektzeit',
        subtitle: 'Projelerinizin saatlerini onaylayın, plan saatlerini yönetin, projeleri değerlendirin ve Forma görevlerini yönlendirin.',
        hint: '"Çalışanlar" sekmesindeki temel bilgilere ek olarak. Proje liderliği proje bazındadır (Proje > Ekip); kendi projelerinizin verilerini görürsünüz.',
      },
      office: {
        label: 'Ofis ve yönetim',
        kicker: 'Ofis, personel ve yönetim için kılavuz',
        title: 'Ofis, personel ve yönetim için Projektzeit',
        subtitle: 'Onaylar, personel, planlama, değerlendirme, faturalandırma, müşteriler, ay kapanışı ve duyurular.',
        hint: '"Çalışanlar" sekmesindeki temel bilgilere ek olarak. Hangi sayfaları gördüğünüzü yetkileriniz belirler; burada rolün ne yaptığı anlatılır.',
      },
      admin: {
        label: 'Teknik yönetici',
        kicker: 'Teknik yönetim kılavuzu',
        title: 'Projektzeit kurulumu ve işletimi',
        subtitle: 'Erişimler, ayarlar, Forma eşitlemesi, yedekleme ve arka plan görevleri.',
        hint: 'Teknik yönetim sistemi kurar ve işletir. Başkalarının saatlerini, ücretleri, personel verilerini ve göstergeleri yalnızca ilgili yetkiyle görür.',
      },
    },
    home: {
      title: 'Başlangıç sayfası, duyurular ve bildirimler',
      lead: 'Bugün ne yapılacak, ne yaklaşıyor ve yeni ne var; yetkilerinize göre.',
      items: [
        {
          title: 'Genel bakış',
          text: 'Başlangıç sayfanız. Üstte sık yaptığınız işler için kutucuklar (zaman kaydı, Haftam; yetkiye göre onaylar, planlama, faturalandırma). **Yapılacaklarım** yalnızca sizi bekleyenleri gösterir: haftayı gönderme, reddedilen kayıtlar, karar bekleyen talepler gibi.',
        },
        {
          title: 'Yaklaşanlar',
          text: 'Kendi devamsızlıklarınız, konumunuzun resmi tatilleri, firma etkinlikleri ve son tarihler, haftayı gönderme süresi ve projelerinizin bitişi. Doğum günleri yalnızca kişi kendisi paylaşmışsa.',
        },
        {
          title: 'Duyurular',
          text: 'Firmadan haberler. "Yeni" okunmamışı işaretler; talimatlarda **Okudum, onaylıyorum** ile onaylarsınız. Acil ve sabitlenenler üstte durur. "Şirket standartları ve şablonlar" firmanın kendi sayfasına götürür; belgeler orada saklanır.',
        },
        {
          title: 'Zil',
          text: 'Sağ üstte: türe göre renkli (saatler, devamsızlıklar, onaylar, sistem …) ve yetkilerinize göre bildirimler. Filtreniz hesabınızda saklanır ve her cihazda geçerlidir. **Geçmişim** kendi son işlemlerinizi gösterir.',
        },
      ],
      note: 'Bildirimler sayfa açıldığında oluşur; bunlar için e-posta gönderilmez. Firma bunu açtıysa ve yayımlayan kişi seçtiyse bir duyuru ayrıca e-postayla da gelir. Geçmişte başkalarının verileri için yalnızca işlemin türü görünür, içeriği asla.',
    },
    quick: {
      title: 'Hızlı kayıt',
      lead: 'Her hafta benzer görevlere kayıt giren birinin artık arama yapması gerekmez.',
      items: [
        { title: 'Son kullanılanlar', text: '"Zaman kaydı" penceresinde en son kayıt girdiğiniz görevler üsttedir: bir tıkla görev seçilir, sonra yalnızca saat girilir.' },
        { title: 'Favoriler', text: '**Yıldız** ile sık kayıt girdiğiniz görevleri saklarsınız (30\'a kadar). Pencerede "Favoriler" altında görünür ve yalnızca size açıktır.' },
        {
          title: 'Birden çok gün',
          text: '**Birden çok güne gir**: aynı görev, aynı saat ve açıklamayla birkaç güne, ör. bir hafta şantiye. Kayıttan önce her günü görürsünüz; resmi tatiller, devamsızlıklar ve zaten kayıt girilmiş günler işaretli gelmez.',
        },
        {
          title: 'Haftayı al',
          text: 'Haftam\'da son haftayı (veya başka bir haftayı) ya da bir **şablonu** (20\'ye kadar, yalnızca size açık) taslak olarak alırsınız. Her satırı kontrol eder, her zamanki gibi gönderirsiniz.',
        },
      ],
      note: 'Her kayıtta olduğu gibi aynı kurallar geçerlidir: saat aralığı, kapatılmış aylar ve faturalanabilir saatlerde açıklama zorunluluğu. Önizlemeniz olmadan hiçbir şey kaydedilmez.',
    },
    external: {
      title: 'Harici çalışan olarak çalışmak',
      lead: 'Hesabınız, proje liderinin veya ofisin size atadığı proje ve görevlerle sınırlıdır.',
      items: [
        { title: 'Neleri görürsünüz', text: 'Atanan proje ve görevlerinizi, kendi saatlerinizi ve haftanızı. Başkalarının saatlerini, ücretleri, göstergeleri ve müşteri bilgilerini görmezsiniz.', who: 'Harici' },
        { title: 'Zaman kaydı', text: 'Görevi seçin, saat ve açıklamayı girin, haftayı gönderin. Taslakları siz gönderene kadar yalnızca sizin hesabınız görür.', who: 'Harici' },
        { title: 'Onay', text: 'Saatlerinizi proje lideri kontrol eder. Reddedilen kayıtta gerekçe yazar: düzeltip yeniden gönderin.', who: 'Proje lideri' },
        { title: 'Görev eksik mi?', text: 'Görevleri proje lideri veya ofis atar. Eksik bir görev varsa projenin proje liderine başvurun.', who: 'Proje lideri, ofis' },
      ],
      note: 'Herkes için geçerli kurallar sizin için de geçerlidir: saat aralığı, kapanmış aylar ve faturalanabilir saatlerde açıklama zorunluluğu.',
    },
    people: {
      title: 'Kişiler, kısaltmalar ve erişim',
      lead: 'Sistem yönetimi > Kişiler: iki sekmeli tek alan; "Kişiler" (ana veriler, rol, erişim) ve "Devamsızlıklar" (haklar, bakiyeler, takvim).',
      items: [
        {
          title: 'Ana veriler',
          text: 'Ad, disiplin, tatil takvimli konum, giriş ve çıkış, bir tarihten itibaren çalışma süresi ve devamsızlıklar için **onaylayan kişi**. Sayfayı yalnızca personel birimi ve kullanıcı yönetimi görür.',
          who: 'personel',
        },
        {
          title: 'Rol ve erişim',
          text: 'Rolü seçin: varsayılan yetkileri belirler; tek tek yetkiler kaldırılabilir veya eklenebilir, unvan bunlardan bağımsızdır. Rol ve yetkileri yalnızca sistem yönetimi ve genel müdürlük değiştirir. Kimse parola dağıtmaz: "Erişimi sıfırla" bir kurulum bağlantısı oluşturur (7 gün, tek kullanımlık). Korunan yetkili hesapları yalnızca bu yetkilere sahip olan değiştirir.',
          who: 'personel, admin',
        },
        {
          title: 'Kısaltma',
          text: 'Herkesin sabit, tüm firmada **benzersiz** 2-6 harf veya rakamlık bir kısaltması vardır: baş harfler (ör. HA); alınmışsa bir ünsüz eklenir (HAL). Kişiler eklense veya ayrılsa da değişmez. "Eksik kısaltmaları ver" boşlukları doldurur.',
          who: 'personel',
        },
        {
          title: 'Doğum günleri',
          text: 'İsteğe bağlı: herkes gün ve ayı **profilinde** kendisi girer ve başkalarının görüp görmeyeceğine kendisi karar verir (ekip takvimi, Yaklaşanlar). Bilerek tam bir liste ve dışa aktarma yoktur.',
        },
      ],
      note: 'Devamsızlıklar, izin hesapları ve resmi tatiller aynı bölümün ikinci sekmesidir, çünkü arkasında aynı kişiler vardır. Yetkiler ayrı kalır: "Kişiler" personel veya kullanıcılar ve roller yetkisiyle, "Devamsızlıklar" personel veya ayarlar yetkisiyle açılır.',
    },
    customers: {
      title: 'Müşteriler ve ödeme koşulları',
      lead: 'Yönetim > Müşteriler, "Müşteri kaydı ve koşullar" yetkisiyle. Sade bir kişi listesi; muhasebe ve fatura kesme yoktur.',
      items: [
        {
          title: 'Kişiler ve firmalar',
          text: 'Firma, adres (şehir, ülke), unvanlı iletişim kişisi, e-posta, telefon, **durum** (Potansiyel müşteri, Teklif verildi, Aktif müşteri, Eski müşteri, İş ortağı, İletişime geçilmeyecek) ve not. Ara, duruma göre filtrele, CSV içe ve dışa aktar.',
        },
        {
          title: 'Firmanın projeleri',
          text: 'Firma başına atanan projeler: rol (işveren, fatura alıcısı …), son 12 ayın saatleri, faturalanmamış iş ve belgeler (net). "İletişime geçilmeyecek": e-posta yok, arama yok.',
        },
        {
          title: 'Ödeme koşulları',
          text: 'Proje başına ("Koşullar" sekmesi): **ödeme vadesi** (ör. 14 gün), **erken ödeme iskontosu** (ör. 7 günde %1,5) ve **KDV sonrası kesintiler** (indirim, teminat kesintisi); hepsi KDV dahil toplam üzerinden. Yeni bir Abschlag (hakediş) belgesi bunları alır; ofis belgede değiştirebilir. Stundennachweis bir saat kanıtıdır, ödeme koşulu içermez.',
        },
      ],
      note: 'İskontoda KDV\'nin düzeltilip düzeltilmeyeceğine ve nasıl düzeltileceğine Steuerberater karar verir (§ 17 UStG). Örnek hesap yalnızca müşterinin ne ödeyeceğini gösterir; faturanın kendisi muhasebede hazırlanır.',
    },
    publish: {
      title: 'Duyuru yayınlama',
      lead: 'Sistem yönetimi > Duyurular, yalnızca "Duyuru yayınlama" yetkisiyle (yönetim, istenirse başka kişiler). Diğer herkes bunları genel bakışta okur.',
      steps: [
        {
          title: 'Tür ve metin',
          text: 'Bilgi, etkinlik, kapalı gün, son tarih, talimat, BT ve sistem veya yönerge değişti. Etkinlik, kapalı gün ve son tarihler ayrıca "Yaklaşanlar" altında görünür.',
          who: 'Tür',
        },
        {
          title: 'Hedef kitle',
          text: 'Tüm çalışanlar, konumlar, roller ("Proje lideri" dahil), proje ekipleri veya **tek tek kişiler** (ör. yalnızca bir kişiye yönelik duyuru). Bir gruba ait olan duyuruyu görür; dış kişiler yalnızca rolünü, projesini veya kendisini ilgilendirenleri.',
          who: 'Kim',
        },
        {
          title: 'Süre ve öncelik',
          text: 'Şu tarihten şu tarihe görünür; önem normal, önemli veya acil; **Sabitle** üstte tutar. Yinelenenler için **Tekrarla** (haftalık, aylık, yıllık), "Önceden göster" ile.',
          who: 'Ne zaman',
        },
        {
          title: 'E-posta ve okuma onayı',
          text: '**E-posta olarak da gönderilsin** (ayarlarda açıksa; her yeni duyuruda başta işaretsiz): duyuru görünür olur olmaz harici kişiler dahil ulaşılan kişilerin şirket adreslerine gider; durum ve önizleme "E-posta" altında. Talimatlar için "Okuduğunu onaylat": kimin henüz onaylamadığını görürsünüz. Diğer tüm duyurularda yalnızca sayılar görünür (… kişi okudu), isim görünmez.',
          who: 'Kanıt',
        },
      ],
      points: [
        '**Firma standartları dışarıda kalır**: yönergeler, şablonlar ve yerleşim düzenleri firmanın sakladığı yerde durur (ör. SharePoint). Duyuru bir **bağlantı** ile oraya yönlendirir; genel bakış ayrıca "Şirket standartları ve şablonlar" bağlantısını gösterir (adres Sistem yönetimi > Ayarlar altında).',
        '**Kısa tutun**: duyuru başına tek mesaj; "Yönerge değişti" için neyin değiştiğini söyleyin ve belgeye bağlantı verin. Onaylı ve tekrarlı talimatlar iş güvenliği ve veri koruma için uygundur.',
      ],
    },
    automation: {
      title: 'Arka plan görevleri ve otomasyon',
      lead: 'Platform rutin işleri kendisi yapar; harici hizmet ve ek lisans gerekmez.',
      items: [
        { title: 'Forma eşitlemesi', text: 'Ayarlanan sıklık ve zaman penceresinde (varsayılan: 15 dakikada bir, her gün 06:00-24:00). Süre ve günlük istekler: Sistem yönetimi > Forma.' },
        { title: 'Outlook', text: 'Bağlantı açıksa 5 dakikada bir: onaylı devamsızlıklar kişisel takvimlere ve ekip takvimine yazılır.' },
        {
          title: 'Gece çalışması 02:30',
          text: 'Tam Forma eşitlemesi, yeni projeleri aktarma (açıksa), veritabanı anlık görüntüsü (35 gece), kapasite değerleri, Power BI dışa aktarımı (kuruluysa), belgeler için saklama süreleri, süresi dolan oturumlar.',
        },
        {
          title: 'Hatırlatmalar',
          text: 'Dört kural (Sistem yönetimi > Ayarlar > Otomasyonlar), tek tek açılıp kapatılır: haftayı gönderme süresi, ay kapanışı, eksik kayıtlar, bekleyen onaylar. İlgili kişide bildirim olarak görünürler.',
        },
        { title: 'Yinelenen duyurular', text: 'Talimatlar, son tarihler veya notlar haftalık, aylık ya da yıllık tekrarlanır (Duyurular). Her tekrar onay için yeniden sayılır.' },
        {
          title: 'Sınırlar',
          text: 'Her kural bir kişiye yalnızca zaten görmeye yetkili olduğunu gösterir; hiçbir otomasyon, onu tetikleyen rolden fazla yetkiye sahip değildir ve kişisel veriler asla yetkisiz kişilere gitmez.',
        },
      ],
      note: 'Otomatik olarak yapılmayanlar: e-postayla periyodik raporlar ve yabancı sistemlerle akışlar (ör. Power Automate). Bu bilerek böyledir: alıcı kitlesi netleşmeden kişisel veri gönderilmez ve ek lisans gerekmez. Yetkili kişiler Excel raporlarını kendileri alır. Tek e-posta: "Duyuruların e-postayla gönderilmesi" açıksa duyurular (vadesi gelenler 5 dakikada bir; yalnızca gönderen posta kutuları için "Application Mail.Send" yetkisi).',
    },
    itNote: {
      title: 'Not: BT talepleri',
      text: 'BT taleplerinizi (erişim, cihaz, yazılım, arıza) şimdilik **Teams** üzerinden BT\'ye iletin. Projektzeit bu talepleri henüz almıyor.',
    },
  },
};
